import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { CSSProperties } from 'react'

import { dismissToast, infoToast, successToast } from '@blattnercompany/ui'
import { Plus } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'

import DeliveryEditDialog from '@/components/deliveries/DeliveryEditDialog'
import type { DeliveryFilters } from '@/components/deliveries/DeliveryFilterBar'
import DeliveryFilterBar from '@/components/deliveries/DeliveryFilterBar'
import DeliveryTileGrid from '@/components/deliveries/DeliveryTileGrid'
import type { DeliveryView } from '@/components/deliveries/DeliveryViewToggle'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useMeasuredHeight } from '@/hooks/useMeasuredHeight'
import { useNow } from '@/hooks/useNow'
import { usePageTitle } from '@/hooks/usePageTitle'
import { useStoredState } from '@/hooks/useStoredState'
import type {
  DeliveryCreate,
  DeliveryDto,
  DeliveryUpdate,
} from '@/services/deliveryService'
import {
  carrierLabel,
  deliveryService,
  isTerminalStage,
} from '@/services/deliveryService'
import type { SiteLocationStatusKind } from '@/services/siteLocationService'
import { siteLocationService } from '@/services/siteLocationService'
import { logger } from '@/utils/logger'
import { matchesRef } from '@/utils/paperworkSearch'
import type { ReschedulePlan } from '@/utils/reschedule'
import {
  announceReschedule,
  planReschedule,
  summariseReschedule,
} from '@/utils/reschedule'
import {
  dayKeyOf,
  dayKeyOffset,
  groupByDay,
  PREVIOUS_DAYS_KEY,
  toDayKey,
  viewForDayKey,
} from '@/utils/scheduleTime'
import { isFullyVisible } from '@/utils/visibility'

import './Deliveries.css'

const log = logger.scoped('Deliveries')

/** Which site locations the destination picker offers: everything but kind
    Retired. Whether Planned locations belong here is still open on #121560 —
    dropping 'Planned' is the whole change. */
const DESTINATION_KINDS: SiteLocationStatusKind[] = ['Planned', 'Active']

type BoardState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'loaded'; deliveries: DeliveryDto[] }

/** Mutates a URLSearchParams to the given view -- shared by setView and any
    caller that has to combine a view change with another param write in a
    single updateParams pass (two sequential updateParams calls would each
    start from the same params and the second would undo the first). */
function applyViewParam(params: URLSearchParams, next: DeliveryView) {
  if (next === 'today') params.delete('view')
  else params.set('view', next)
}

/** Mutates a URLSearchParams to drop every board filter -- shared by
    clearFilters and any caller combining it with another param write in a
    single updateParams pass. See applyViewParam. */
function applyClearFilters(params: URLSearchParams) {
  params.delete('carrier')
  params.delete('vendor')
  params.delete('ref')
}

/**
 * Sorted distinct values for one field, for the pickers that offer them.
 * Read from the whole board rather than from the current view, so narrowing
 * the view can never delete the option a filter is already set to.
 */
function distinctValues(
  deliveries: DeliveryDto[],
  field: 'carrier' | 'vendor' | 'driver' | 'destination',
): string[] {
  return [...new Set(deliveries.map((delivery) => delivery[field]))]
    .filter((value) => value !== '')
    .sort()
}

/**
 * The board with a landed write applied to one row -- the time through
 * planReschedule, same as the rail popover, and every other field merged in
 * as given. Pure, so the modal writes can run it inside a functional
 * setState and always build on the latest board: a toast's Undo or Show me
 * stays clickable while a dialog is open, so the board can move under an
 * in-flight write.
 */
function applyDeliveryChanges(
  deliveries: DeliveryDto[],
  id: string,
  changes: DeliveryUpdate,
): DeliveryDto[] {
  const { scheduledFor, ...rest } = changes
  let next = deliveries
  if (scheduledFor) {
    next = planReschedule(next, id, scheduledFor)?.next ?? next
  }
  if (Object.keys(rest).length > 0) {
    next = next.map((delivery) =>
      delivery.id === id ? { ...delivery, ...rest } : delivery,
    )
  }
  return next
}

type PendingMove = {
  id: string
  /** Time to restore if the user undoes. */
  previousTime: string
  /** Toast text, used only when the landing spot is unfollowable. */
  summary: string
  /** A cross-day move is never animated, so it always toasts. */
  crossedDay: boolean
}

function Deliveries() {
  usePageTitle('Deliveries')

  const [state, setState] = useState<BoardState>({ status: 'loading' })
  // Failure just leaves this empty -- destinations then fall back to
  // whatever the board already uses (see `options` below), so a location
  // service hiccup degrades to today's behaviour rather than blocking the
  // page.
  const [siteLocations, setSiteLocations] = useState<string[]>([])
  const [announcement, setAnnouncement] = useState('')
  const [pendingMove, setPendingMove] = useState<PendingMove | null>(null)
  const [undoTarget, setUndoTarget] = useState<PendingMove | null>(null)

  const [editing, setEditing] = useState<DeliveryDto | null>(null)
  /** The Add form is the edit dialog with no record behind it. */
  const [adding, setAdding] = useState(false)
  /** Row to scroll to once the view containing it has rendered. */
  const [pendingScrollId, setPendingScrollId] = useState<string | null>(null)

  // The view and the filters live in the URL so a refresh keeps them and a
  // bookmarked "?view=previous&carrier=Dakota+Freight+Lines" is a saved triage
  // screen. Today is the field default.
  const [searchParams, setSearchParams] = useSearchParams()
  const viewParam = searchParams.get('view')
  const view: DeliveryView =
    viewParam === 'week' || viewParam === 'month' || viewParam === 'previous'
      ? viewParam
      : 'today'

  /**
   * One write for every control in the strip, built from the current params
   * rather than replacing them — so changing the view cannot drop the
   * filters or the reverse. Every caller mutates in a single pass: two
   * sequential writes would each start from the same params, and the second
   * would undo the first. `replace` keeps the back button pointed at the
   * page you arrived from rather than at every control you touched.
   */
  const updateParams = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current)
          mutate(next)
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  const setView = useCallback(
    (next: DeliveryView) =>
      updateParams((params) => applyViewParam(params, next)),
    [updateParams],
  )

  /**
   * A value that matches no delivery stays applied rather than being dropped
   * — the empty state explains it and Clear fixes it. Silently ignoring it
   * would make a shared link show something other than what it says.
   */
  const carrierFilter = searchParams.get('carrier') ?? undefined
  const vendorFilter = searchParams.get('vendor') ?? undefined
  const refFilter = searchParams.get('ref') ?? undefined
  const filters: DeliveryFilters = {
    carrier: carrierFilter,
    vendor: vendorFilter,
    ref: refFilter,
  }
  const hasFilters = Boolean(carrierFilter || vendorFilter || refFilter)

  /**
   * Applying a filter widens the board — to Month (30 days) by default, the
   * widest the board goes.
   *
   * Filtering is an act of searching, and searching inside Today can only
   * report what today holds — the row being hunted slips past and nothing
   * says so, so applying one has to widen the window it searches. For the
   * same reason it also unfolds the Previous day(s) section — see
   * foldPreviousDays below.
   *
   * Clearing does not touch the view: putting a filter down is not
   * searching, so it has no business moving you.
   *
   * The paperwork lookup rides the same path and is the strongest case for
   * it: a BOL or PO number is a precise query about one truck. Unlike
   * carrier/vendor, it identifies a single row, so handleFilterChange below
   * narrows the widen target to wherever that row actually is rather than
   * defaulting here to Month.
   */
  const setFilter = useCallback(
    (
      key: keyof DeliveryFilters,
      value: string | undefined,
      view: DeliveryView = 'month',
    ) =>
      updateParams((params) => {
        if (!value) {
          params.delete(key)
          return
        }
        params.set(key, value)
        params.set('view', view)
      }),
    [updateParams],
  )

  const clearFilters = useCallback(
    () => updateParams(applyClearFilters),
    [updateParams],
  )

  /**
   * Folding Previous day(s) is a standing preference, not a per-visit toggle:
   * for the field the section is noise most days, and re-folding it every
   * morning would be its own kind of noise. Default is expanded — nobody gets
   * outstanding trucks folded away by a default they never chose.
   *
   * Interim storage while there is no settings page; the key is namespaced
   * like stagehand.projectId so the eventual preferences provider can reuse it
   * and nobody's fold silently resets. See useStoredState.
   */
  const [previousDaysFolded, setPreviousDaysFolded] = useStoredState(
    'stagehand.previousDaysCollapsed',
    false,
  )

  const togglePreviousDays = useCallback(
    () => setPreviousDaysFolded(!previousDaysFolded),
    [previousDaysFolded, setPreviousDaysFolded],
  )

  const boardRef = useRef<HTMLDivElement>(null)
  // The board owns at most one toast. Held by id rather than by state because
  // the toast lives in the shared store now -- this ref is only how the page
  // takes back the one it put there.
  const toastIdRef = useRef<string | null>(null)

  /** Takes back the board's toast, if it still has one up. */
  const clearBoardToast = useCallback(() => {
    if (toastIdRef.current === null) return
    dismissToast(toastIdRef.current)
    toastIdRef.current = null
  }, [])

  // The control strip is sticky, so it covers the top of the scrollport and
  // the day headings have to pin below it. Its height is measured rather
  // than tokenised because the strip wraps at narrow widths — one value,
  // published to the CSS below and to the toast's visibility test, so the
  // two can never disagree.
  const { ref: stripRef, height: stripHeight } = useMeasuredHeight()

  // One clock for the page: drives the Previous day(s) grouping, the
  // Today / Tomorrow headings (stale after midnight otherwise), and each
  // tile's overdue tag -- all from the same reading of the time.
  const now = useNow()

  useEffect(() => {
    const controller = new AbortController()

    deliveryService
      .getScheduled(controller.signal)
      .then((deliveries) => setState({ status: 'loaded', deliveries }))
      .catch((error: unknown) => {
        // An abort is this component unmounting, not a failure -- surfacing
        // it as one would flash an error on every fast navigation.
        if (controller.signal.aborted) return
        log.error('Failed to load scheduled deliveries', { error })
        setState({ status: 'error' })
      })

    return () => controller.abort()
  }, [])

  useEffect(() => {
    const controller = new AbortController()

    siteLocationService
      .list({ kinds: DESTINATION_KINDS }, controller.signal)
      .then((locations) =>
        setSiteLocations(locations.map((location) => location.name)),
      )
      .catch((error: unknown) => {
        if (controller.signal.aborted) return
        log.error('Failed to load project site locations', { error })
      })

    return () => controller.abort()
  }, [])

  /** Every picker's option list: the strip's two filters, the dialog's two
      selects. One pass over the board, one place to look.
      Destinations union the site-location list with whatever the board
      already uses -- never dropping a destination a delivery already has,
      the same reasoning distinctValues itself is built on, and the only way
      the edit dialog's destination <select> is guaranteed to still contain
      the record it is editing. */
  const options = useMemo(() => {
    const deliveries = state.status === 'loaded' ? state.deliveries : []
    return {
      carriers: distinctValues(deliveries, 'carrier'),
      vendors: distinctValues(deliveries, 'vendor'),
      receivers: distinctValues(deliveries, 'driver'),
      destinations: [
        ...new Set([
          ...siteLocations,
          ...distinctValues(deliveries, 'destination'),
        ]),
      ].sort(),
    }
  }, [state, siteLocations])

  const todayKey = dayKeyOf(now)
  const weekEndKey = dayKeyOffset(now, 7)
  const monthEndKey = dayKeyOffset(now, 30)

  /**
   * The view filter. Previous day(s) rides along in every view -- an
   * outstanding truck is never silently hidden -- while terminal past rows
   * are history in all of them (that is the History page's job someday).
   * Nothing renders beyond today + 30 anywhere: the 30-day view is the far
   * edge of what the yard plans against, and Week is the day-to-day slice
   * inside it ("we thought next Monday, it is here today").
   */
  const visibleDeliveries = useMemo(() => {
    if (state.status !== 'loaded') return []
    return state.deliveries.filter((delivery) => {
      const dayKey = toDayKey(delivery.scheduledFor)
      if (dayKey < todayKey) return !isTerminalStage(delivery.stage)
      if (view === 'previous') return false
      if (view === 'today') return dayKey === todayKey
      if (view === 'month') return dayKey <= monthEndKey
      return dayKey <= weekEndKey
    })
  }, [state, view, todayKey, weekEndKey, monthEndKey])

  /**
   * The paperwork lookup's candidate pool: the whole board, minus rows
   * visibleDeliveries above would never render in any view -- a terminal
   * delivery (Received, Refused, Cancelled) from a previous day is history,
   * permanently. Offering one as a search result would be a dead end:
   * selecting it applies a ref filter that matches nothing on the board.
   */
  const searchableDeliveries = useMemo(() => {
    if (state.status !== 'loaded') return []
    return state.deliveries.filter(
      (delivery) =>
        toDayKey(delivery.scheduledFor) >= todayKey ||
        !isTerminalStage(delivery.stage),
    )
  }, [state, todayKey])

  /**
   * Counts come from the whole board, so no view can hide the maroon one —
   * and neither can folding the section, since the fold is applied further
   * down at render time and never touches these numbers.
   *
   * Today is a subset of Week is a subset of 30 days, so each number counts
   * the narrower ones too: they read as "how much is in scope in this view",
   * not as a partition of the board. Previous day(s) is the exception -- it
   * counts only outstanding rows from previous days, which is why it can be
   * the loud one while the others look calm.
   */
  const viewCounts = useMemo(() => {
    if (state.status !== 'loaded')
      return { today: 0, week: 0, month: 0, previous: 0 }
    let today = 0
    let week = 0
    let month = 0
    let previous = 0
    for (const delivery of state.deliveries) {
      const dayKey = toDayKey(delivery.scheduledFor)
      if (dayKey < todayKey) {
        if (!isTerminalStage(delivery.stage)) previous += 1
      } else if (dayKey <= monthEndKey) {
        month += 1
        if (dayKey <= weekEndKey) {
          week += 1
          if (dayKey === todayKey) today += 1
        }
      }
    }
    return { today, week, month, previous }
  }, [state, todayKey, weekEndKey, monthEndKey])

  /**
   * The strip's filters, applied after the view and as their own pass. The
   * view answers "when", a filter answers "whose", and the toggle's counts
   * are computed before either — so a filtered board can never make one of
   * those numbers disappear.
   */
  const filteredDeliveries = useMemo(() => {
    if (!carrierFilter && !vendorFilter && !refFilter) return visibleDeliveries
    return visibleDeliveries.filter(
      (delivery) =>
        (!carrierFilter || delivery.carrier === carrierFilter) &&
        (!vendorFilter || delivery.vendor === vendorFilter) &&
        (!refFilter || matchesRef(delivery, refFilter)),
    )
  }, [visibleDeliveries, carrierFilter, vendorFilter, refFilter])

  const days = useMemo(
    () => groupByDay(filteredDeliveries, now),
    [filteredDeliveries, now],
  )

  /**
   * The fold as rendered, which is not the same as the fold as stored.
   *
   * A filter forces the section open: filtering is searching, and a search
   * that silently skips the stray trucks answers a narrower question than the
   * one asked — the same reasoning that widens the view to Week. The Previous
   * day(s) view ignores the fold outright, since the section is that view's
   * entire content and honouring it would render an empty board.
   *
   * Neither override writes to the preference, so searching or switching views
   * can never quietly change a setting the user made.
   */
  const foldPreviousDays =
    previousDaysFolded && !hasFilters && view !== 'previous'

  /** Rows the board will actually paint — a folded section contributes none. */
  const renderedRowCount = useMemo(
    () =>
      days.reduce(
        (total, day) =>
          total +
          (foldPreviousDays && day.dayKey === PREVIOUS_DAYS_KEY
            ? 0
            : day.deliveries.length),
        0,
      ),
    [days, foldPreviousDays],
  )

  /**
   * Applied to local state first so the board moves the instant it is tapped,
   * then reported to the service. Nothing rolls back on failure yet — there
   * is no endpoint to fail, and inventing a rollback now would be guessing at
   * behaviour the real write path has to define.
   */
  /** Announce a schedule move and queue the toast decision. Shared by the
      rail popover and the modal, so a cross-day edit gets the same
      toast / Undo treatment as "Just arrived". */
  const applyScheduleFeedback = useCallback(
    (plan: ReschedulePlan<DeliveryDto>, feedback: boolean) => {
      setAnnouncement(announceReschedule(plan, now))

      const move: PendingMove = {
        id: plan.id,
        previousTime: plan.from,
        summary: summariseReschedule(plan, now),
        crossedDay: plan.crossedDay,
      }
      // Undo restores the previous working ETA only -- stage and promisedFor
      // were never touched by a reschedule.
      setUndoTarget(move)

      // An undo is itself a reschedule, but one the user just asked for by
      // name -- announce it, never toast it.
      if (feedback && plan.moved) {
        setPendingMove(move)
      } else {
        clearBoardToast()
      }
    },
    [now, clearBoardToast],
  )

  const handleReschedule = useCallback(
    (id: string, scheduledFor: string, feedback = true) => {
      // Reads state directly rather than inside a setState updater: the
      // side-effects (announcement, undo target, pending move) must not live
      // in an updater, which StrictMode double-invokes.
      if (state.status !== 'loaded') return

      const plan = planReschedule(state.deliveries, id, scheduledFor)
      if (!plan) return

      setState({ status: 'loaded', deliveries: plan.next })
      applyScheduleFeedback(plan, feedback)

      void deliveryService
        .reschedule(id, scheduledFor)
        .catch((error: unknown) => {
          log.error('Failed to reschedule delivery', {
            id,
            scheduledFor,
            error,
          })
        })
    },
    [state, applyScheduleFeedback],
  )

  /**
   * A modal save. Not optimistic, on purpose -- unlike the rail popover's
   * reschedule (instant, low-stakes, easy to Undo), this is a full field
   * edit behind an explicit Save; the tile should only change once the
   * write has actually landed, so the board never shows an edit that then
   * silently reverts. DeliveryEditDialog awaits the returned promise and
   * shows its own loading/error state around this.
   */
  const handleSaveEdit = useCallback(
    (id: string, changes: DeliveryUpdate): Promise<void> => {
      if (state.status !== 'loaded') return Promise.resolve()
      if (Object.keys(changes).length === 0) return Promise.resolve()

      return deliveryService.update(id, changes).then(() => {
        // The reschedule/Undo feedback is planned against the closed-over
        // board, out here where it can fire -- read-only, and React doesn't
        // guarantee an updater has run by the line after its setState. The
        // write itself stays a functional update, so it lands on the latest
        // board rather than overwriting whatever moved while it was in
        // flight (see applyDeliveryChanges).
        const feedbackPlan =
          changes.scheduledFor && state.status === 'loaded'
            ? planReschedule(state.deliveries, id, changes.scheduledFor)
            : null
        setState((current) =>
          current.status === 'loaded'
            ? {
                status: 'loaded',
                deliveries: applyDeliveryChanges(
                  current.deliveries,
                  id,
                  changes,
                ),
              }
            : current,
        )
        if (feedbackPlan) applyScheduleFeedback(feedbackPlan, true)
      })
    },
    [state, applyScheduleFeedback],
  )

  /**
   * Office work on a truck that never arrived: ETA -> Cancelled. Not
   * optimistic, on purpose: unlike an ordinary edit, a cancel that silently
   * fails to land is a real inconsistency -- everyone stops expecting the
   * truck locally while the record is still active on the server. The
   * promise is returned, not swallowed, so DeliveryEditDialog's Cancel panel
   * can wait for it and let the user retry on failure instead of finding out
   * later that the cancel never took.
   *
   * `edits` are the Cancel panel's "also save my changes" -- sent in the same
   * write as the cancel, so the two land or fail together and there is no
   * partial state to explain.
   */
  const handleCancelDelivery = useCallback(
    (id: string, reason: string, edits: DeliveryUpdate = {}) => {
      const changes: DeliveryUpdate = {
        ...edits,
        stage: 'Cancelled',
        statusNote: reason,
      }
      return deliveryService.update(id, changes).then(() => {
        // The toast's label is read from the closed-over board (read-only,
        // so safe outside the updater); an edited carrier rides in with
        // `edits`. The write itself is functional -- see handleSaveEdit.
        const before =
          state.status === 'loaded'
            ? state.deliveries.find((delivery) => delivery.id === id)
            : undefined
        setState((current) =>
          current.status === 'loaded'
            ? {
                status: 'loaded',
                deliveries: applyDeliveryChanges(
                  current.deliveries,
                  id,
                  changes,
                ),
              }
            : current,
        )
        setAnnouncement(`${id} cancelled`)
        if (before) {
          successToast(
            `${carrierLabel(edits.carrier ?? before.carrier)} cancelled`,
          )
        }
      })
    },
    [state],
  )

  /**
   * The view change needed to widen far enough to contain the given day, or
   * undefined if the current view already shows it -- never narrows a view
   * that already shows it. Returned rather than applied directly so a
   * caller that also has to change filters (the add-delivery toast) can
   * combine both into one updateParams pass instead of two -- see
   * applyViewParam/applyClearFilters above. Shared by Show me
   * (reschedule/undo, below) and the add-delivery toast, so the two
   * "take me to this row" paths cannot drift apart.
   */
  const revealDay = useCallback(
    (dayKey: string): DeliveryView | undefined => {
      if (dayKey === todayKey && view === 'previous') return 'today'
      if (dayKey > todayKey) {
        // Only switch if the current view doesn't already show the row —
        // the board reaches 30 days now, so a fixed widen target (Week)
        // could either fail to reach a row past +7 or, from Month, narrow
        // away from a row that was already visible.
        const alreadyVisible =
          (view === 'week' && dayKey <= weekEndKey) ||
          (view === 'month' && dayKey <= monthEndKey)
        if (!alreadyVisible) {
          return viewForDayKey(dayKey, todayKey, weekEndKey)
        }
      }
      // A dialog date edit can push a row back into Previous day(s). Folded,
      // that section has no rows to scroll to and Show me would silently do
      // nothing -- so unfold. This one override *does* write the preference,
      // unlike the filter and view overrides: the user asked to be taken to
      // this row, and the section they land in is visibly open rather than
      // quietly changed underneath them. Not part of the URL params, so it
      // carries no race with the view/filter writes above.
      else if (dayKey < todayKey && previousDaysFolded) {
        setPreviousDaysFolded(false)
      }
      return undefined
    },
    [
      todayKey,
      view,
      weekEndKey,
      monthEndKey,
      previousDaysFolded,
      setPreviousDaysFolded,
    ],
  )

  /**
   * The add-delivery toast's Show me action reads the view and filters
   * through this ref rather than closing over them directly: the toast can
   * sit on screen for a while after create() resolves, and the user may
   * switch view or change a filter before clicking it. Reading the ref at
   * click time gets whatever is current, not whatever was current when the
   * delivery was saved. Same pattern as handleUndoRef below, same reason.
   */
  const revealStateRef = useRef({
    revealDay,
    carrierFilter,
    vendorFilter,
    refFilter,
    monthEndKey,
  })
  useEffect(() => {
    revealStateRef.current = {
      revealDay,
      carrierFilter,
      vendorFilter,
      refFilter,
      monthEndKey,
    }
  }, [revealDay, carrierFilter, vendorFilter, refFilter, monthEndKey])

  /**
   * A hand-entered delivery. The one write that is not optimistic: the row
   * does not exist until the service assigns its id, so the board waits for
   * the response and then places the row -- into whichever day it belongs
   * to, with no refresh. The promise is returned, not swallowed here --
   * DeliveryEditDialog's add form awaits it and keeps the dialog open with
   * every value intact on failure, rather than losing the entered data to a
   * toast that just says "try again."
   *
   * No Undo action -- entering several deliveries in a row is a real
   * workflow, and yanking the view to each one as it saves would fight it.
   * Show me is opt-in instead: it widens the view, drops any filter that
   * would hide the new row, and scrolls to it, but only on click.
   */
  const handleCreateDelivery = useCallback(
    (input: DeliveryCreate) => {
      return deliveryService.create(input).then((created) => {
        // A functional update is safe here: no side-effects live inside it.
        setState((current) =>
          current.status === 'loaded'
            ? {
                status: 'loaded',
                deliveries: [...current.deliveries, created],
              }
            : current,
        )
        setAnnouncement(`${created.id} added`)
        successToast(`${carrierLabel(created.carrier)} added to the board`, {
          actions: [
            {
              label: 'Show me',
              onClick: () => {
                const {
                  revealDay,
                  carrierFilter,
                  vendorFilter,
                  refFilter,
                  monthEndKey,
                } = revealStateRef.current
                const dayKey = toDayKey(created.scheduledFor)
                // Past the board's 30-day horizon, no view can show it —
                // nothing to widen to or scroll toward. Read from the ref,
                // not the closure -- the toast can outlive the day it was
                // shown on, same reasoning as revealDay/the filters above.
                const nextView =
                  dayKey <= monthEndKey ? revealDay(dayKey) : undefined
                if (dayKey <= monthEndKey) setPendingScrollId(created.id)
                const hidden =
                  (carrierFilter && created.carrier !== carrierFilter) ||
                  (vendorFilter && created.vendor !== vendorFilter) ||
                  (refFilter && !matchesRef(created, refFilter))
                // One updateParams pass for both, not two -- see
                // applyViewParam/applyClearFilters above.
                if (nextView || hidden) {
                  updateParams((params) => {
                    if (nextView) applyViewParam(params, nextView)
                    if (hidden) applyClearFilters(params)
                  })
                }
              },
            },
          ],
        })
      })
    },
    [updateParams],
  )

  const handleUndo = useCallback(() => {
    if (undoTarget) {
      handleReschedule(undoTarget.id, undoTarget.previousTime, false)
    }
    setUndoTarget(null)
    clearBoardToast()
  }, [handleReschedule, undoTarget, clearBoardToast])

  /**
   * The live toast's Undo action is registered once, by the effect below,
   * and only re-registered when a second reschedule re-keys the toast --
   * any other state change in between (say, a cancel) leaves it pointing at
   * the handleUndo closure from whenever the toast first appeared. That
   * closure's handleReschedule call reads `state` directly (see its own
   * comment on why), so a stale Undo would rebuild the board from a
   * snapshot that predates the cancel and silently revive it.
   *
   * This ref is updated every render, unconditionally, so onClick can read
   * `handleUndoRef.current` at click time and always get the latest one --
   * cheaper than making the toast-registration effect itself re-run on
   * every unrelated state change.
   */
  const handleUndoRef = useRef(handleUndo)
  useEffect(() => {
    handleUndoRef.current = handleUndo
  }, [handleUndo])

  const handleShowMe = useCallback(() => {
    clearBoardToast()
    if (!undoTarget || state.status !== 'loaded') return

    // The row may have moved out of the current view (Just arrived from the
    // Previous day(s) view lands in Today). Switch to a view that shows it,
    // then scroll once that view has rendered.
    const target = state.deliveries.find((d) => d.id === undoTarget.id)
    if (target) {
      const nextView = revealDay(toDayKey(target.scheduledFor))
      if (nextView) setView(nextView)
    }
    setPendingScrollId(undoTarget.id)
  }, [undoTarget, state, revealDay, setView, clearBoardToast])

  /**
   * Wraps setFilter for the strip's onFilterChange: carrier/vendor and a
   * ref with no (or a Previous day(s)) match fall through to setFilter's
   * default Month widen, but a ref that matches exactly one row — the
   * paperwork lookup's whole point — widens only as far as that row needs,
   * the same reasoning as handleShowMe above. See viewForDayKey.
   */
  const handleFilterChange = useCallback(
    (key: keyof DeliveryFilters, value: string | undefined) => {
      if (key === 'ref' && value && state.status === 'loaded') {
        const match = state.deliveries.find((d) => matchesRef(d, value))
        if (match) {
          const dayKey = toDayKey(match.scheduledFor)
          if (dayKey >= todayKey) {
            setFilter(key, value, viewForDayKey(dayKey, todayKey, weekEndKey))
            return
          }
        }
      }
      setFilter(key, value)
    },
    [state, setFilter, todayKey, weekEndKey],
  )

  // Scrolls after the (possibly switched) view has rendered its rows.
  useLayoutEffect(() => {
    if (!pendingScrollId) return
    boardRef.current
      ?.querySelector<HTMLElement>(`[data-flip-key="${pendingScrollId}"]`)
      ?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    setPendingScrollId(null)
  }, [pendingScrollId, days])

  /**
   * Feedback decision, after the DOM has settled in its new order. Runs on
   * offsetTop arithmetic (see utils/visibility), so any FLIP animation a day
   * section may have started cannot corrupt the measurement.
   *
   * - crossed a day: never animated, so always toast
   * - moved within its day, landing off-screen: toast
   * - moved within its day, visibly: the slide is its own confirmation
   *
   * Placed below the handlers because it hands them to the toast as actions,
   * and re-runs harmlessly when they change: `pendingMove` is cleared here, so
   * every run but the one that set it early-returns.
   */
  useLayoutEffect(() => {
    if (!pendingMove) return

    const moved = boardRef.current?.querySelector<HTMLElement>(
      `[data-flip-key="${pendingMove.id}"]`,
    )
    const followable =
      !pendingMove.crossedDay &&
      moved &&
      isFullyVisible(moved, '.main', stripHeight)

    if (followable) {
      clearBoardToast()
    } else {
      // One `key` for the board, so a flurry of reschedules replaces the
      // notice instead of stacking it. Undo goes through handleUndoRef
      // (above) rather than closing over handleUndo directly, since this
      // effect does not re-run for every state change that could make that
      // closure stale -- only a second reschedule re-keys the toast.
      toastIdRef.current = infoToast(pendingMove.summary, {
        key: 'delivery-reschedule',
        // The page keeps its own persistent aria-live for a reschedule
        // (below); announcing here as well would double-speak it.
        announce: false,
        actions: [
          { label: 'Undo', onClick: () => handleUndoRef.current() },
          { label: 'Show me', onClick: handleShowMe },
        ],
      })
    }

    setPendingMove(null)
  }, [pendingMove, handleShowMe, clearBoardToast, stripHeight])

  return (
    <div className="willfade">
      <div className="pagehead">
        <div>
          <h1>Deliveries</h1>
        </div>
        {/* Loads arranged by phone or email never reach the board from
            upstream -- this is how they get on it. Disabled outside
            'loaded': handleCreateDelivery's functional update only merges
            the created row when state.status is 'loaded', so a save while
            still loading (or after a failed load) would succeed against
            the service and then be silently dropped until a refresh. */}
        <Button
          type="button"
          onClick={() => setAdding(true)}
          disabled={state.status !== 'loaded'}
          title={
            state.status === 'loading'
              ? 'Waiting for the delivery board to load'
              : state.status === 'error'
                ? 'Deliveries could not be loaded — try again shortly'
                : undefined
          }
        >
          <Plus aria-hidden="true" />
          Add delivery
        </Button>
      </div>

      {state.status === 'loading' ? (
        <div className="delivery-board__loading" aria-busy="true">
          <span className="sr-only">Loading deliveries</span>
          {[0, 1, 2].map((row) => (
            <Skeleton key={row} className="h-28 w-full rounded-lg" />
          ))}
        </div>
      ) : null}

      {/* A visible reason, never a silently empty board -- an empty list and
          a failed fetch look identical otherwise. */}
      {state.status === 'error' ? (
        <p className="delivery-board__message delivery-board__message--error">
          Deliveries could not be loaded. The schedule service may be
          unavailable — try again shortly.
        </p>
      ) : null}

      {state.status === 'loaded' ? (
        <DeliveryFilterBar
          view={view}
          onViewChange={setView}
          counts={viewCounts}
          filters={filters}
          carriers={options.carriers}
          vendors={options.vendors}
          deliveries={searchableDeliveries}
          now={now}
          onFilterChange={handleFilterChange}
          stripRef={stripRef}
        />
      ) : null}

      {state.status === 'loaded' &&
      days.length === 0 &&
      state.deliveries.length === 0 ? (
        <p className="delivery-board__message">
          No deliveries are scheduled for this project yet.
        </p>
      ) : null}

      {/* A filter emptied the board, so "check 30 days or Previous day(s)"
          would be wrong advice — say what happened and offer the way out.

          Three wordings, because the boundary the user just hit differs.
          In the 30-day view they have hit the board's horizon and there is
          nowhere wider to look. Applying a filter lands them in Week, which
          is no longer the widest — so it names the number it stopped at and
          points further out. Getting here in Today or Previous day(s) takes
          narrowing back down afterwards, and 30 days is then the place the
          matches may be. */}
      {state.status === 'loaded' &&
      days.length === 0 &&
      state.deliveries.length > 0 &&
      hasFilters ? (
        <div className="delivery-board__message">
          <p className="delivery-board__message-text">
            {view === 'month'
              ? 'Nothing matches these filters in the next 30 days.'
              : view === 'week'
                ? 'Nothing matches these filters in the next 7 days — try 30 days.'
                : 'Nothing matches these filters in this view — try 30 days.'}
          </p>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={clearFilters}
          >
            Clear filters
          </button>
        </div>
      ) : null}

      {state.status === 'loaded' &&
      days.length === 0 &&
      state.deliveries.length > 0 &&
      !hasFilters ? (
        <p className="delivery-board__message">
          Nothing in this view — check 30 days or Previous day(s).
        </p>
      ) : null}

      {days.length > 0 ? (
        <div
          ref={boardRef}
          style={{ '--delivery-strip-h': `${stripHeight}px` } as CSSProperties}
        >
          <DeliveryTileGrid
            days={days}
            now={now}
            onReschedule={handleReschedule}
            onEdit={setEditing}
            foldPreviousDays={foldPreviousDays}
            onTogglePreviousDays={togglePreviousDays}
          />
        </div>
      ) : null}

      {/* The board has sections but paints no rows, which only happens when
          the folded Previous day(s) section is all there was. The folded
          header above already carries the count, so this says what is missing
          rather than pointing at the section sitting right there. */}
      {days.length > 0 && renderedRowCount === 0 ? (
        <p className="delivery-board__message delivery-board__message--aside">
          {view === 'today'
            ? 'Nothing else scheduled for today.'
            : view === 'month'
              ? 'Nothing else scheduled in the next 30 days.'
              : 'Nothing else scheduled in the next 7 days.'}
        </p>
      ) : null}

      {editing || adding ? (
        <DeliveryEditDialog
          delivery={editing ?? undefined}
          carriers={options.carriers}
          vendors={options.vendors}
          receivers={options.receivers}
          destinations={options.destinations}
          onSave={handleSaveEdit}
          onCreate={handleCreateDelivery}
          onCancelDelivery={handleCancelDelivery}
          onClose={() => {
            setEditing(null)
            setAdding(false)
          }}
        />
      ) : null}

      {/* Announced whether or not a toast shows, so a reschedule is never
          silent for a screen-reader user. */}
      <p aria-live="polite" className="sr-only">
        {announcement}
      </p>
    </div>
  )
}

export default Deliveries
